import {
  collection,
  doc,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  type CollectionReference,
  type FirestoreError,
  type Unsubscribe,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { notificationDocumentConverter } from "@kekkle/shared/convertors/web/notification-document.converter";
import type { NotificationDocument } from "@kekkle/shared/types/documents/notification-document/notification-document";
import {
  NOTIFICATION_COLLECTION_PATH,
  USER_COLLECTION_PATH,
} from "@kekkle/shared/constants/collections";
import { handleFirestoreException } from "@/helpers/exceptions/handler";

/**
 * Returns notifications collection reference with converter for a given user
 */
function userNotificationsCollection(
  userId: string,
): CollectionReference<NotificationDocument> {
  return collection(
    firestore,
    USER_COLLECTION_PATH,
    userId,
    NOTIFICATION_COLLECTION_PATH,
  ).withConverter(notificationDocumentConverter);
}

import type { SnapshotMetadata } from "@/types/firestore/metadata";
export type { SnapshotMetadata };

/**
 * Subscribe to user notifications in real-time ordered by created_at descending
 */
export function subscribeToUserNotifications(
  userId: string,
  onUpdate: (
    notifications: NotificationDocument[],
    metadata: SnapshotMetadata,
  ) => void,
  onError?: (error: FirestoreError) => void,
): Unsubscribe {
  const q = query(
    userNotificationsCollection(userId),
    orderBy("created_at", "desc"),
  );

  return onSnapshot(
    q,
    { includeMetadataChanges: true },
    (snapshot) => {
      const docs = snapshot.docs.map((docSnap) => docSnap.data());
      onUpdate(docs, {
        fromCache: snapshot.metadata.fromCache,
        hasPendingWrites: snapshot.metadata.hasPendingWrites,
      });
    },
    (error) => {
      if (onError) onError(error);
      else handleFirestoreException({ e: error });
    },
  );
}

/**
 * Mark a single notification as read
 */
export async function markNotificationAsRead(
  userId: string,
  notificationId: string,
): Promise<void> {
  try {
    const docRef = doc(userNotificationsCollection(userId), notificationId);
    await updateDoc(docRef, {
      is_read: true,
      read_at: serverTimestamp(),
    });
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Mark all given notifications as read
 */
export async function markAllNotificationsAsRead(
  userId: string,
  notificationIds: string[],
): Promise<void> {
  try {
    const promises = notificationIds.map((id) =>
      markNotificationAsRead(userId, id),
    );
    await Promise.all(promises);
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Delete a notification
 */
export async function deleteNotificationDoc(
  userId: string,
  notificationId: string,
): Promise<void> {
  try {
    const docRef = doc(userNotificationsCollection(userId), notificationId);
    await deleteDoc(docRef);
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}
